import { MediaStateItem, MediaStateResponse, HistoryEpisode } from './media-state.types'

export type MediaState = {
  data: MediaStateResponse | null
  loaded: boolean
}

export type MediaStateAction =
  | { type: 'set'; payload: MediaStateResponse | null }
  | { type: 'loaded' }
  | { type: 'add_favorite'; payload: MediaStateItem }
  | { type: 'remove_favorite'; payload: number }
  | { type: 'add_watchlist'; payload: MediaStateItem }
  | { type: 'remove_watchlist'; payload: number }
  | { type: 'add_history'; payload: MediaStateItem }
  | { type: 'remove_history'; payload: number }
  | { type: 'add_episode'; payload: { showId: number; episode: HistoryEpisode; showMeta: MediaStateItem } }
  | { type: 'remove_episode'; payload: { showId: number; episodeId: number } }
  | { type: 'add_season_episodes'; payload: { showId: number; episodes: HistoryEpisode[]; showMeta: MediaStateItem } }
  | { type: 'remove_season_episodes'; payload: { showId: number; seasonNumber: number } }

export const mediaStateReducer = (state: MediaState, action: MediaStateAction): MediaState => {
  const data = state.data ?? { favorites: [], watchlist: [], history: [] }

  switch (action.type) {
    case 'set':
      return { ...state, data: action.payload, loaded: true }
    case 'loaded':
      return { ...state, loaded: true }

    case 'add_favorite': {
      const currentStats = data.stats ?? {}
      return {
        ...state,
        data: {
          ...data,
          favorites: [action.payload, ...data.favorites],
          stats: { ...currentStats, totalFavorites: (currentStats.totalFavorites ?? 0) + 1 },
        },
      }
    }
    case 'remove_favorite': {
      const currentStats = data.stats ?? {}
      const existed = data.favorites.some((f) => f.tmdbId === action.payload)
      return {
        ...state,
        data: {
          ...data,
          favorites: data.favorites.filter((f) => f.tmdbId !== action.payload),
          stats: existed
            ? { ...currentStats, totalFavorites: Math.max(0, (currentStats.totalFavorites ?? 0) - 1) }
            : currentStats,
        },
      }
    }

    case 'add_watchlist': {
      const currentStats = data.stats ?? {}
      return {
        ...state,
        data: {
          ...data,
          watchlist: [action.payload, ...data.watchlist],
          stats: { ...currentStats, totalWatchlist: (currentStats.totalWatchlist ?? 0) + 1 },
        },
      }
    }
    case 'remove_watchlist': {
      const currentStats = data.stats ?? {}
      const existed = data.watchlist.some((w) => w.tmdbId === action.payload)
      return {
        ...state,
        data: {
          ...data,
          watchlist: data.watchlist.filter((w) => w.tmdbId !== action.payload),
          stats: existed
            ? { ...currentStats, totalWatchlist: Math.max(0, (currentStats.totalWatchlist ?? 0) - 1) }
            : currentStats,
        },
      }
    }

    case 'add_history': {
      // Only movies bump the movie count; shows are counted via their episodes.
      const currentStats = data.stats ?? {}
      const isMovie = action.payload.mediaType === 'Movie'
      return {
        ...state,
        data: {
          ...data,
          history: [action.payload, ...data.history],
          stats: isMovie
            ? { ...currentStats, totalMoviesWatched: (currentStats.totalMoviesWatched ?? 0) + 1 }
            : currentStats,
        },
      }
    }
    case 'remove_history': {
      const currentStats = data.stats ?? {}
      const removed = data.history.find((h) => h.tmdbId === action.payload)
      const isMovie = removed?.mediaType === 'Movie'
      return {
        ...state,
        data: {
          ...data,
          history: data.history.filter((h) => h.tmdbId !== action.payload),
          stats: isMovie
            ? { ...currentStats, totalMoviesWatched: Math.max(0, (currentStats.totalMoviesWatched ?? 0) - 1) }
            : currentStats,
        },
      }
    }

    case 'add_episode': {
      const { showId, episode, showMeta } = action.payload
      const existingShow = data.history.find((h) => h.tmdbId === showId)
      const isRegularEpisode = episode.season !== 0
      const currentStats = data.stats ?? {}
      const updatedStats = isRegularEpisode
        ? { ...currentStats, totalEpisodesWatched: (currentStats.totalEpisodesWatched ?? 0) + 1 }
        : currentStats

      if (existingShow) {
        const newEpisodes = [...(existingShow.episodes ?? []), episode]
        const newEpisodesWatchedCount = newEpisodes.filter((e) => e.season !== 0).length
        return {
          ...state,
          data: {
            ...data,
            stats: updatedStats,
            history: data.history.map((h) =>
              h.tmdbId === showId
                ? { ...h, episodes: newEpisodes, episodesWatchedCount: newEpisodesWatchedCount }
                : h
            ),
          },
        }
      }
      return {
        ...state,
        data: {
          ...data,
          stats: updatedStats,
          history: [{ ...showMeta, episodes: [episode], episodesWatchedCount: isRegularEpisode ? 1 : 0 }, ...data.history],
        },
      }
    }

    case 'remove_episode': {
      const { showId, episodeId } = action.payload
      const show = data.history.find((h) => h.tmdbId === showId)
      if (!show) return state
      const removedEpisode = (show.episodes ?? []).find((e) => e.episodeId === episodeId)
      const isRegularEpisode = removedEpisode && removedEpisode.season !== 0
      const currentStats = data.stats ?? {}
      const updatedStats = isRegularEpisode
        ? { ...currentStats, totalEpisodesWatched: Math.max(0, (currentStats.totalEpisodesWatched ?? 0) - 1) }
        : currentStats

      const remainingEpisodes = (show.episodes ?? []).filter((e) => e.episodeId !== episodeId)
      if (remainingEpisodes.length === 0) {
        return {
          ...state,
          data: { ...data, stats: updatedStats, history: data.history.filter((h) => h.tmdbId !== showId) },
        }
      }
      const newEpisodesWatchedCount = remainingEpisodes.filter((e) => e.season !== 0).length
      return {
        ...state,
        data: {
          ...data,
          stats: updatedStats,
          history: data.history.map((h) =>
            h.tmdbId === showId
              ? { ...h, episodes: remainingEpisodes, episodesWatchedCount: newEpisodesWatchedCount }
              : h
          ),
        },
      }
    }

    case 'add_season_episodes': {
      const { showId, episodes, showMeta } = action.payload
      const existingShow = data.history.find((h) => h.tmdbId === showId)
      const currentStats = data.stats ?? {}

      if (existingShow) {
        const existingEpisodeIds = new Set((existingShow.episodes ?? []).map((e) => e.episodeId))
        const newEpisodes = episodes.filter((e) => !existingEpisodeIds.has(e.episodeId))
        const newRegularEpisodesCount = newEpisodes.filter((e) => e.season !== 0).length
        const updatedStats = {
          ...currentStats,
          totalEpisodesWatched: (currentStats.totalEpisodesWatched ?? 0) + newRegularEpisodesCount,
        }
        const allEpisodes = [...(existingShow.episodes ?? []), ...newEpisodes]
        const newEpisodesWatchedCount = allEpisodes.filter((e) => e.season !== 0).length
        return {
          ...state,
          data: {
            ...data,
            stats: updatedStats,
            history: data.history.map((h) =>
              h.tmdbId === showId
                ? { ...h, episodes: allEpisodes, episodesWatchedCount: newEpisodesWatchedCount }
                : h
            ),
          },
        }
      }
      const regularEpisodesCount = episodes.filter((e) => e.season !== 0).length
      const updatedStats = {
        ...currentStats,
        totalEpisodesWatched: (currentStats.totalEpisodesWatched ?? 0) + regularEpisodesCount,
      }
      return {
        ...state,
        data: {
          ...data,
          stats: updatedStats,
          history: [{ ...showMeta, episodes, episodesWatchedCount: regularEpisodesCount }, ...data.history],
        },
      }
    }

    case 'remove_season_episodes': {
      const { showId, seasonNumber } = action.payload
      const show = data.history.find((h) => h.tmdbId === showId)
      if (!show) return state
      const currentStats = data.stats ?? {}
      const removedEpisodes = (show.episodes ?? []).filter((e) => e.season === seasonNumber)
      const removedRegularCount = removedEpisodes.filter((e) => e.season !== 0).length
      const updatedStats = {
        ...currentStats,
        totalEpisodesWatched: Math.max(0, (currentStats.totalEpisodesWatched ?? 0) - removedRegularCount),
      }

      const remainingEpisodes = (show.episodes ?? []).filter((e) => e.season !== seasonNumber)
      if (remainingEpisodes.length === 0) {
        return {
          ...state,
          data: { ...data, stats: updatedStats, history: data.history.filter((h) => h.tmdbId !== showId) },
        }
      }
      const newEpisodesWatchedCount = remainingEpisodes.filter((e) => e.season !== 0).length
      return {
        ...state,
        data: {
          ...data,
          stats: updatedStats,
          history: data.history.map((h) =>
            h.tmdbId === showId
              ? { ...h, episodes: remainingEpisodes, episodesWatchedCount: newEpisodesWatchedCount }
              : h
          ),
        },
      }
    }

    default:
      return state
  }
}

export const initialMediaState: MediaState = { data: null, loaded: false }
